import React from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, TextField, Typography } from '@mui/material';
import { Formik, FormikErrors } from 'formik';
import CloseIcon from '@mui/icons-material/Close';
import ButtonComponent from 'src/components/ButtonComponent';
import IOSSwitch from 'src/components/IosSwitch';

import { App, CreateAppPayload, UpdateAppPayload } from 'src/dto/App';
import { getActiveCompany } from 'src/frontend-common/localstorage';
import CustomAutoComplete from 'src/frontend-common/components/HorizontalMenu/CustomAutocomplete/index';
import { ItemProps } from 'src/frontend-common/components/HorizontalMenu/CustomAutocomplete';
import { CompanyOption, useGetCompaniesOptions } from 'src/hooks/useGetCompaniesOptions';
import { CompanyDetail } from 'src/frontend-common/dto/Interfaces/ICompany';
interface FormValues {
  name: string;
  active: boolean;
  allow_negative_balance: boolean;
  max_enrichment_time: number;
  company: { value: string; label: string } | null;
}

export interface AppCreateModalProps {
  open: boolean;
  onClose: () => void;
  onUpdate: (a: UpdateAppPayload) => void;
  onCreate: (values: CreateAppPayload) => void;
  initData: App | null;
  activeCompany: string;
}

export const AppCreateModal: React.FC<AppCreateModalProps> = ({ open, onClose, onCreate, onUpdate, initData }) => {
  const activeCompany = getActiveCompany();

  const [company, setNewCompany] = React.useState(initData?.company_id || activeCompany?.id || '');

  const companies = useGetCompaniesOptions();

  const getCompanyById = (id: string): CompanyDetail | undefined => {
    const [companyList] = companies[0] || [];

    if (Array.isArray(companyList)) {
      return companyList.find((c: CompanyOption) => c.key === id);
    }

    return undefined;
  };

  return (
    <Formik
      initialValues={{
        name: initData ? initData.name : '',
        active: initData ? initData.active : true,
        company: initData
          ? {
              value: initData.company_id,
              label: getCompanyById(initData.company_id)?.name || '',
            }
          : activeCompany,
        allow_negative_balance: initData ? initData.allow_negative_balance : false,
        max_enrichment_time: initData ? initData.max_enrichment_time : null,
      }}
      enableReinitialize
      validateOnChange={true}
      validate={(values) => {
        const errors: FormikErrors<FormValues> = {};

        if (values.max_enrichment_time !== null && (isNaN(values.max_enrichment_time) || values.max_enrichment_time < 1.6 || values.max_enrichment_time > 10)) {
          errors.max_enrichment_time = 'Must be a number between 1.6 and 10, or left empty';
        }

        if (values.name.trim().length === 0) {
          errors.name = 'Required';
        }
        if (initData && typeof values.company === 'string') {
          errors.company = 'Required';
        }

        return errors;
      }}
      // @ts-ignore
      onSubmit={(values: FormValues, { resetForm }) => {
        // in case we don't have any init data we serve the create form
        if (!initData) {
          onCreate({
            name: values.name,
            allow_negative_balance: values.allow_negative_balance,
            active: values.active,
          });

          resetForm();
          return;
        }

        // additional checks for consistencies
        if (!values.company) {
          return null;
        }

        onUpdate({
          app_key: initData.app_key,
          allow_negative_balance: values.allow_negative_balance,
          name: values.name,
          active: values.active,
          max_enrichment_time: values.max_enrichment_time,
          id: initData.id,
          owner_company_id: company,
        });
        resetForm();
        return;
      }}
    >
      {(formikProps) => {
        const {
          errors: { name: nameError, max_enrichment_time: maxEnrichmentTimeError },
          touched: { name: nameTouched },
        } = formikProps;

        return (
          <Dialog fullWidth={true} maxWidth="sm" open={open} onClose={onClose}>
            <DialogTitle>
              <Grid container justifyContent="space-between" alignItems="center">
                <Typography variant="h4">App key</Typography>
                <ButtonComponent icon={<CloseIcon />} buttontype="icon" onClick={onClose} />
              </Grid>
            </DialogTitle>

            <Divider light />

            <DialogContent style={{ overflow: 'hidden', paddingTop: 25 }}>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  formikProps.handleSubmit();
                }}
              >
                <Grid container={true} spacing={3}>
                  <Grid item={true} xs={12}>
                    <TextField
                      variant="outlined"
                      label="Name"
                      type="text"
                      error={nameTouched && !!nameError}
                      fullWidth={true}
                      {...formikProps.getFieldProps('name')}
                    />
                  </Grid>

                  {initData && (
                    <Grid item={true} xs={12} style={{ display: 'flex', justifyContent: 'center' }}>
                      <CustomAutoComplete
                        items={companies[0] as CompanyOption[]}
                        activeItem={activeCompany?.name || ''}
                        setItem={(newValue: ItemProps | null) => {
                          let newCompany = undefined;
                          if (companies[1]) {
                            const noDuplicatedCompanies = companies[1] as {
                              id: string;
                              name: string;
                            }[];
                            newCompany = noDuplicatedCompanies.find((c) => c.id === (newValue as any)?.key);
                          }

                          if (newCompany) {
                            setNewCompany(newCompany.id);
                          }
                        }}
                        noPadding
                        textFieldTitle="Company"
                        isLoading={companies[0]?.length === 0}
                      />
                    </Grid>
                  )}
                  <Grid item={true} xs={12}>
                    <TextField
                      variant="outlined"
                      label="Max Enrichment Time (in seconds)"
                      type="number"
                      inputProps={{ step: 0.1, min: 1.6, max: 10 }}
                      error={!!maxEnrichmentTimeError}
                      helperText={maxEnrichmentTimeError}
                      fullWidth
                      value={formikProps.values.max_enrichment_time ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        formikProps.setFieldValue('max_enrichment_time', value === '' ? null : parseFloat(value));
                      }}
                    />
                  </Grid>

                  <Grid item={true} xs={12}>
                    <Typography variant="h5">Status</Typography>
                    <IOSSwitch
                      sx={{ marginTop: '10px' }}
                      color="primary"
                      checked={formikProps.getFieldProps('active').value}
                      {...formikProps.getFieldProps('active')}
                    />
                  </Grid>
                  <Grid item={true} xs={12}>
                    <Typography variant="h5">Allow negative balance</Typography>
                    <IOSSwitch
                      sx={{ marginTop: '10px' }}
                      color="primary"
                      checked={formikProps.getFieldProps('allow_negative_balance').value}
                      {...formikProps.getFieldProps('allow_negative_balance')}
                    />
                  </Grid>
                </Grid>
              </form>
            </DialogContent>

            <Divider light />

            <DialogActions>
              <ButtonComponent buttontype="cancel" onClick={onClose} style={{ marginRight: 16 }} />

              <ButtonComponent buttontype="mainaction" text="Save" onClick={() => formikProps.submitForm()} />
            </DialogActions>
          </Dialog>
        );
      }}
    </Formik>
  );
};
